// React and related libraries
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ComponentStateProvider } from 'swipeclock-frontend';

// Routing
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import { createLazyRoute, wfhIndexActions } from 'lib/router';

// Redux store
import { initializeStore } from 'store/store';
import { rootReducer } from 'store/reducers';

// Config
import config from 'data/config';

// Hooks
import { useUserShuttleSubscriptions } from 'hooks/useShuttle';
import { useAppendScriptForSwipeclockUser } from 'hooks/useSwipeclockUser';

// Providers
import SnackbarProvider from 'providers/SnackbarProvider';
import SwipeclockUserBootstrap from 'providers/SwipeclockUserBootstrap';

// Components
import SwipeclockTheme from 'components/SwipeclockTheme';
import TopAppBar from 'components/TopAppBar';
import UserDrawer from 'components/Drawer/UserDrawer';

// Pages
import AcceptInvite, {
  action as acceptInviteAction,
  loader as acceptInviteLoader,
} from 'pages/public/AcceptInvite';
import ErrorBoundary from 'pages/ErrorBoundary';
import FreeTrial from 'pages/public/FreeTrial/FreeTrial';
import Login from 'pages/public/Login';
import PasswordReset, {
  action as resetPasswordAction,
  loader as resetPasswordLoader,
} from 'pages/public/PasswordReset';

// Free Trial Pages
import ErrorsPage from 'pages/public/FreeTrial/pages/ErrorsPage';
import LandingPage from 'pages/public/FreeTrial/pages/LandingPage';
import FormPage from 'pages/public/FreeTrial/pages/FormPage';
import CheckYourEmailPage from 'pages/public/FreeTrial/pages/CheckYourEmailPage';
import EmailVerifiedPage from 'pages/public/FreeTrial/pages/EmailVerifiedPage';
import BasicInfoPage, {
  loader as basicInfoLoader,
} from 'pages/public/FreeTrial/pages/BasicInfoPage';
import SiteCreating from 'pages/public/FreeTrial/pages/SiteCreating';
import SiteReady from 'pages/public/FreeTrial/pages/SiteReady';

import AcknowledgeStripePayment, {
  loader as AcknowldgeStripePaymentLoader,
} from 'pages/public/AcknowledgeStripePayment';

const store = initializeStore(rootReducer);

const root = ReactDOM.createRoot(document.getElementById('shell-root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <SwipeclockTheme>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ComponentStateProvider>
            <SnackbarProvider>
              <RouterProvider
                router={createBrowserRouter(
                  [
                    // ###################
                    // Public Routes
                    // ###################
                    {
                      path: '/stripe-acknowledge-payment',
                      element: <AcknowledgeStripePayment />,
                      loader: ({ params, request }) =>
                        AcknowldgeStripePaymentLoader(store, {
                          params,
                          request,
                        }),
                      errorElement: <ErrorBoundary />,
                    },
                    {
                      path: '/acceptInvite/:employeeId/:guid',
                      element: <AcceptInvite />,
                      errorElement: <ErrorBoundary />,
                      action: ({ params, request }) => {
                        return acceptInviteAction(store, { params, request });
                      },
                      loader: ({ params, request }) => {
                        return acceptInviteLoader(store, { params, request });
                      },
                    },
                    {
                      path: '/passwordReset/:employeeId/:guid',
                      element: <PasswordReset />,
                      errorElement: <ErrorBoundary />,
                      action: ({ params, request }) => {
                        return resetPasswordAction(store, { params, request });
                      },
                      loader: ({ params, request }) => {
                        return resetPasswordLoader(store, { params, request });
                      },
                    },
                    {
                      path: '/login',
                      element: <Login />,
                      errorElement: <ErrorBoundary />,
                    },
                    {
                      path: '/freeTrial',
                      element: <FreeTrial />,
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          element: <LandingPage />,
                          index: true,
                        },
                        {
                          path: 'form',
                          element: <FormPage />,
                        },
                        {
                          path: 'check-your-email',
                          element: <CheckYourEmailPage />,
                        },
                        {
                          path: 'verify',
                          element: <EmailVerifiedPage />,
                        },
                        {
                          path: 'basic-info',
                          element: <BasicInfoPage />,
                          loader: () => {
                            return basicInfoLoader(store);
                          },
                        },
                        {
                          path: 'error/:errorType?',
                          element: <ErrorsPage />,
                        },
                        {
                          path: 'site-creating',
                          element: <SiteCreating />,
                        },
                        {
                          path: 'site-ready',
                          element: <SiteReady />,
                        },
                      ],
                    },
                    // ##################
                    // Private Routes
                    // ##################
                    {
                      path: '/agreements',
                      errorElement: <ErrorBoundary />,
                      async lazy() {
                        return createLazyRoute('Agreements', store);
                      },
                    },
                    {
                      path: '/',
                      element: <PrivateLayout />,
                      errorElement: <ErrorBoundary />,
                      action: ({ params, request }) => {
                        return wfhIndexActions(store, { params, request });
                      },
                      children: [
                        {
                          index: true,
                          element: <Navigate to="/dashboard" replace />,
                        },
                        {
                          path: 'dashboard',
                          async lazy() {
                            return createLazyRoute('Dashboard', store);
                          },
                        },
                        {
                          path: 'employee-record',
                          async lazy() {
                            return createLazyRoute('EmployeeRecord', store);
                          },
                        },
                        // ##################
                        // WorkforceHub Routes
                        // ##################
                        {
                          path: ':tab',
                          async lazy() {
                            return createLazyRoute('WorkforceHub', store);
                          },
                        },
                        // Note same as /:tab, but with a drawer url parameter.
                        // This may seem redundant, but it is following the pattern advised by React Router Team.
                        {
                          path: ':tab/:drawer',
                          async lazy() {
                            return createLazyRoute('WorkforceHub', store);
                          },
                        },
                      ],
                    },
                    // #################
                    // Error Routes
                    // #################
                    {
                      path: '/error',
                      element: <ErrorBoundary />,
                    },
                    {
                      path: '*',
                      element: <Navigate to="/error?status=404" replace />,
                    },
                  ],
                  {
                    basename: config.publicPath,
                  }
                )}
              />
            </SnackbarProvider>
          </ComponentStateProvider>
        </LocalizationProvider>
      </SwipeclockTheme>
    </Provider>
  </React.StrictMode>
);

function PrivateLayout() {
  useAppendScriptForSwipeclockUser();
  useUserShuttleSubscriptions();
  return (
    <SwipeclockUserBootstrap name="workforcehub">
      <TopAppBar />
      <UserDrawer showLogo />
      <Outlet />
    </SwipeclockUserBootstrap>
  );
}
